import React, { Fragment, useEffect, useMemo, useRef, useState } from 'react';
import { Box, Button, Container, Dialog, Grid, useMediaQuery } from '@mui/material';
import { DriveEta as CarIcon } from '@mui/icons-material';
import DOMPurify from 'dompurify';
import Handlebars from 'handlebars';
import _ from 'lodash';
import { inject, observer } from 'mobx-react';
import moment from 'moment';
import { useNavigate } from 'react-router-dom';
import { compose } from 'utils/compose';

import PODThumbDown from 'components/Dialog/PODThumbDown';
import { DAY_IN_MILLISECONDS } from 'constants/common';
import { EVENT_TEMPLATE_OWNERS } from 'constants/eventTemplate';
import DialogFeedback from 'containers/main/Dialog/DialogFeedback';
import { resolveClientDisplayName } from 'utils/client';
import { transformEvent } from 'utils/events';
import { eventDescriptionStyle } from 'utils/eventDescriptionStyle';
import * as SHIPMENT_STATUS from 'constants/shipmentStatus';
import { VERIFICATION } from 'constants/verification';
import useSearchParams from 'hooks/useSearchParams';
import { getCookie, setCookie } from 'utils/cookie';
import PreviewImage from '../PreviewImage';
import DialogStepOne from 'containers/main/Dialog/DialogStepOne';
import DialogStepTwo from 'containers/main/Dialog/DialogStepTwo';
import DialogStepThree from './Dialogs/DialogStepThree';
import EventTimelineIcon from './EventTimelineIcon';
import {
  capitalizeText,
  convertMeterToMileString,
  convertSecondToHour,
  formatDateTime,
  lowerCase,
  removeDash,
  replaceDash,
  splitText,
  upperCase,
  when,
  whenAnd,
  whenOr,
  stringReplace,
} from 'utils/handlebars';

import 'slick-carousel/slick/slick-theme.css';
import 'slick-carousel/slick/slick.css';
import './slick.css';
import ClickTooltip from '../ClickTooltip';

const customFunctions = [
  {
    func: lowerCase,
    name: 'lowerCase',
  },
  {
    func: upperCase,
    name: 'upperCase',
  },
  {
    func: removeDash,
    name: 'removeDash',
  },
  {
    func: replaceDash,
    name: 'replaceDash',
  },
  {
    func: convertMeterToMileString,
    name: 'convertMeterToMileString',
  },
  {
    func: convertSecondToHour,
    name: 'convertSecondToHour',
  },
  {
    func: capitalizeText,
    name: 'capitalizeText',
  },
  {
    func: splitText,
    name: 'splitText',
  },
  {
    func: formatDateTime,
    name: 'formatDateTime',
  },
  {
    func: when,
    name: 'when',
  },
  {
    func: whenAnd,
    name: 'whenAnd',
  },
  {
    func: whenOr,
    name: 'whenOr',
  },
  {
    func: stringReplace,
    name: 'stringReplace',
  },
];

customFunctions.map((func) => Handlebars.registerHelper(func.name, func.func));

function Event(props) {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { classes, delivery, translatedEvents, deliveryStore, podStore, closeDialog } = props;

  const [showDialogNext, setShowDialogNext] = useState(0);
  const [open, setOpen] = useState(false);
  const [isPreview, setIsPreview] = useState(false);
  const imageRef = useRef(null);
  const [hasToken, setHasToken] = useState(false);
  const [images, setImages] = useState([]);
  const [currentSlide, setCurrentSlide] = useState(0);
  const [isThumbDown, setIsThumbDown] = useState(false);
  const [isOpenFeedback, setIsOpenFeedback] = useState(false);
  const [events, setEvents] = useState([]);

  const { shipment, outbound_events, client, dropoff, client_profile, settings, shipmentImageAnnotations, eco_friendly, approved_pod_number } =
    delivery;
  const enablePOD = settings?.enable_pod?.toLocaleLowerCase() === 'true';
  const verifyByShipmentInfo = settings?.enabled_verify_by_shipment_info === 'true';
  const isDropoffSucceed =
    shipmentImageAnnotations.some((sia) => sia.stop_id === dropoff?.id && sia.status === 'ACCEPTED') && dropoff?.status === 'SUCCEEDED';

  const isEcoFriendly = !!eco_friendly;

  const milestones = outbound_events.map((e) => e.milestone).filter(Boolean);
  const milestone = milestones[0] || SHIPMENT_STATUS.PROCESSING;
  const isDelivered = milestone === SHIPMENT_STATUS.DELIVERED;

  const renderText = (event) => event.description;
  const isMobile = useMediaQuery((theme) => theme.breakpoints.down('sm'));
  const clientId = client?.id || 0;
  const clientName = resolveClientDisplayName(client, client_profile);

  const fetchContentList = async () => {
    const owners = [EVENT_TEMPLATE_OWNERS.DEFAULT, `${EVENT_TEMPLATE_OWNERS.CLIENT}${clientId}`];
    const templates = await deliveryStore.getEventTemplates(owners);

    if (!templates.ok) {
      setEvents([]);
      return;
    }
    setEvents(
      transformEvent(
        translatedEvents?.map((evt) => ({
          ...evt,
          client,
          client_profile,
        })),
        templates,
        owners,
      ),
    );
  };

  useEffect(() => {
    fetchContentList();
  }, []);

  useEffect(() => {
    let token = searchParams.get('axl_shipment_token') || searchParams.get('t');
    if (!token) token = deliveryStore.token || getCookie('axl_shipment_token');

    if (!token) setHasToken(false);

    if (token) {
      try {
        deliveryStore.getTokenInfo(token, (res) => {
          if (res.ok) {
            setHasToken(true);
            const ttl = _.get(res, 'data.ttl', 0);
            setCookie('axl_shipment_token', token, ttl / DAY_IN_MILLISECONDS);

            if (enablePOD) {
              deliveryStore.getFeedback();
              deliveryStore.getPOD((res) => {
                if (!res.ok || (res.data && (res.data.code === 404 || res.data.code === 500))) {
                  setImages([]);
                  podStore.setImagesPOD([]);
                } else {
                  // Validate that res.data is an array before setting it
                  const images = Array.isArray(res.data) ? res.data : [];
                  setImages(images);
                  podStore.setImagesPOD(images);
                  if (images.length > 0) {
                    const isShowPOD = searchParams.get('pod');
                    if (isShowPOD === 1 || isShowPOD?.toLocaleLowerCase() === 'true') {
                      handlePreviewImage(null, 0);
                    }
                    deliveryStore.getPackageLocation();
                  }
                }
              });
            }
          } else {
            deliveryStore.updateField('token', null);
            navigate(`/${deliveryStore.trackingCode}`, { replace: true });
            setCookie('axl_shipment_token', '', 0);
            setHasToken(false);
          }
        });
      } catch (error) {
        setHasToken(false);
        deliveryStore.updateField('token', null);
        navigate(`/${deliveryStore.trackingCode}`, { replace: true });
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [deliveryStore, enablePOD, podStore, searchParams, isDelivered, navigate]);

  useEffect(() => {
    const token = deliveryStore.token || getCookie('axl_shipment_token');
    setHasToken(!!token);
    if (!isMobile && [1, 2].includes(podStore.currentStep) && podStore.isPODFeedback) {
      setOpen(true);
      setShowDialogNext(podStore.currentStep);
    }
  }, [deliveryStore.token, isMobile, podStore.currentStep, podStore.isPODFeedback]);

  useMemo(() => {
    setImages(podStore.imagesPOD);
  }, [podStore.imagesPOD]);

  const handlePhotoOfDelivery = (e) => {
    e.preventDefault();
    setOpen(true);
    setShowDialogNext(1);
    podStore.updateCurrentStep(1);
    podStore.updateField('isPODFeedback', true);
  };

  const handleCloseDialog = () => {
    setOpen(false);
    setShowDialogNext(0);
    deliveryStore.setCodeVerified();
    deliveryStore.setVerificationMethod(VERIFICATION.SMS);
    podStore.updateCurrentStep(0);
    podStore.updateIsRequested(false);
  };

  const handleNext = (v) => {
    setShowDialogNext(v);
    podStore.updateCurrentStep(v);
  };

  const setToken = (valToken, time, step) => {
    setCookie('axl_shipment_token', valToken, time);
    podStore.updateField('currentStep', step);
    podStore.updateField('isPODFeedback', true);
    if (isMobile || step === 4) {
      deliveryStore.openDialog(4);
    }
  };

  const handlePreviewImage = (e, idx) => {
    const token = getCookie('axl_shipment_token');
    if (verifyByShipmentInfo) {
      setIsPreview(true);
      return;
    }

    if (!token) {
      setToken('', 0, 1);
      podStore.setImagesPOD([]);
      return;
    }
    if (_.isEmpty(deliveryStore.feedback)) {
      try {
        deliveryStore.getTokenInfo(token, (res) => {
          if (res.ok) {
            const ttl = _.get(res, 'data.ttl', 0);
            setToken(token, ttl / DAY_IN_MILLISECONDS, 4);
          } else {
            setToken('', 0, 1);
            podStore.setImagesPOD([]);
          }
        });
      } catch (error) {
        podStore.setImagesPOD([]);
      }
      return;
    }
    setIsPreview(true);
    setCurrentSlide(idx);
    setTimeout(() => {
      if (imageRef.current) imageRef.current.slickGoTo(idx);
    }, 0);
    podStore.updateField('isPODFeedback', true);
  };

  const handleClosePreview = () => {
    setIsPreview(false);
    podStore.updateField('isPODFeedback', false);
    podStore.updateField('isPreviewImage', false);
    if (!podStore.isChanged || podStore.isDisplayed) {
      return;
    }

    if (images?.some((s) => s.hasOwnProperty('thumb') && !s.thumb)) {
      setIsThumbDown(true);
      podStore.updateField('isDisplayed', false);
      podStore.updateField('isChanged', false);
      return;
    }
  };

  const handleRequestPOD = (e) => {
    e.preventDefault();
    setOpen(true);
    setShowDialogNext(1);
    podStore.updateCurrentStep(1);
    podStore.updateIsRequested(true);
  };

  // const handleCloseRequestPOD = () => {
  //   setIsShowRequestPOD(false);
  // }

  const closePopUp = () => {
    setIsThumbDown(false);
    podStore.updateField('isDisplayed', false);
    podStore.updateField('isChanged', false);
  };

  const handleCloseFeedback = () => {
    setIsOpenFeedback(false);
    deliveryStore.setStep(1);
    podStore.updateField('isDisplayed', false);
    podStore.updateField('isChanged', false);
    deliveryStore.getFeedback();
  };

  const handleClose = () => {
    setOpen(false);
    podStore.updateCurrentStep(0);
  };
  const canRequestPOD = (mstone) => {
    //temporary turn off
    // const isHavePODAnnotation = delivery?.shipmentImageAnnotations.some(sia => sia.stop_id == delivery?.dropoff?.id);
    // if (SHIPMENT_STATUS.DELIVERED === mstone && enablePOD && !hasAccepted && !isAlreadyRequested && isHavePODAnnotation ){
    //   return true;
    // }
    return false;
  };

  return (
    <>
      <Grid size={{ xs: 12, sm: 6 }} sx={classes.bodyItemWrapper}>
        <Container sx={{ ...classes.bodyBottomItem, ...classes.boxWrapper }}>
          {events?.map(({ origin, converted, template }, idx) => {
            const text = renderText(origin);

            if (!text || _.isEmpty(template)) return null;

            const eventDate = moment(origin.ts)
              .tz(shipment.timezone || 'America/Los_Angeles')
              .format('MMM DD');
            const eventTime = moment(origin.ts)
              .tz(shipment.timezone || 'America/Los_Angeles')
              .format('hh:mm A');
            const mstone = origin.milestone;

            let compileContent = Handlebars.compile(template.content);
            const rendered = compileContent({ ...converted, ...origin, shipment, client, dropoff, client_name: clientName });

            const displayPOD = SHIPMENT_STATUS.DELIVERED === mstone && enablePOD && isDropoffSucceed && approved_pod_number > 0;
            const { category, type, signal } = origin;
            const eventTestId = [category, type, signal].filter(Boolean).join('.');

            return (
              <Fragment key={`event-${idx}`}>
                <Grid container sx={classes.deliveryStep} data-testid={`event-${eventTestId}`}>
                  <Grid size={3} sx={{ ...classes.deliveryStepCell, ...classes.deliveryStepCellDateTime }}>
                    <Box sx={classes.deliveryStepDate} data-testid="event-date">
                      {eventDate}
                    </Box>
                    <Box sx={classes.deliveryStepTime} data-testid="event-time">
                      {eventTime}
                    </Box>
                  </Grid>
                  <Grid
                    size={1}
                    sx={{
                      ...classes.deliveryStepCell,
                      ...classes.deliveryStepCellIcon,
                      ...(idx + 1 < translatedEvents.length && classes.deliveryStepConnector),
                    }}
                  >
                    <EventTimelineIcon event={origin} color={template.color} />
                  </Grid>
                  <Grid size={9} sx={classes.deliveryStepCell} data-testid="event-content">
                    <Box
                      sx={eventDescriptionStyle(origin)}
                      dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(rendered) }}
                    />
                    {displayPOD && images?.length === 0 && (
                      <div style={{ fontWeight: 600, marginTop: '8px' }}>
                        <span>You may view your </span>
                        <Box component="span" sx={classes.hyperlink} onClick={handlePhotoOfDelivery}>
                          Photo of Delivery
                        </Box>
                        <span> here.</span>
                      </div>
                    )}

                    {displayPOD && images?.length > 0 && (
                      <Fragment>
                        <Box component="span" sx={{ ...classes.textPrimary, textDecoration: 'underline', fontWeight: 500 }}>
                          Below is your Photo of Delivery:
                        </Box>
                        <Box style={{ marginTop: '8px', display: 'flex' }}>
                          {images?.map((m, idx) => {
                            if (idx < 2) {
                              return (
                                <img
                                  alt=""
                                  src={m?.url}
                                  style={{
                                    width: '33.333%',
                                    marginRight: '5px',
                                    cursor: 'pointer',
                                    flex: '0 0 33.3333%',
                                    maxHeight: isMobile ? '100px' : '100%',
                                  }}
                                  onClick={(e) => handlePreviewImage(e, idx)}
                                  key={`image-${idx}`}
                                />
                              );
                            }

                            if (idx === 2) {
                              return (
                                <div style={{ display: 'flex', position: 'relative', flex: '0 0 33.3333%' }} key={`image-${idx}`}>
                                  {images.length - 3 > 0 && (
                                    <span
                                      style={{
                                        position: 'absolute',
                                        top: '50%',
                                        left: '50%',
                                        transform: 'translate(-50%, -50%)',
                                        fontSize: '1.5rem',
                                        color: 'white',
                                        cursor: 'pointer',
                                        fontWeight: 700,
                                        zIndex: 1,
                                      }}
                                      onClick={(e) => handlePreviewImage(e, idx)}
                                    >
                                      +{images.length - 2}
                                    </span>
                                  )}
                                  <img
                                    alt=""
                                    src={m?.url}
                                    style={{
                                      width: '33.3333%',
                                      marginRight: '5px',
                                      cursor: 'pointer',
                                      filter: images.length - 3 > 0 ? 'brightness(0.7)' : '',
                                      WebkitFilter: images.length - 3 > 0 ? 'brightness(0.7)' : '',
                                      flex: '0 0 100%',
                                      maxHeight: isMobile ? '100px' : '100%',
                                    }}
                                    onClick={(e) => handlePreviewImage(e, idx)}
                                  />
                                </div>
                              );
                            }

                            return null;
                          })}
                        </Box>
                      </Fragment>
                    )}

                    {SHIPMENT_STATUS.DELIVERED === mstone && isEcoFriendly && (
                      <ClickTooltip title={delivery?.extra_text?.eco_friendly} dataTestId="event-eco-friendly">
                        <Box ml={1} mt={1} style={{ cursor: 'pointer' }}>
                          <CarIcon style={{ verticalAlign: 'bottom', marginRight: 4 }} />
                          <span>
                            Delivered by an <u>🌱 eco-friendly vehicle</u>
                          </span>
                        </Box>
                      </ClickTooltip>
                    )}

                    {!hasToken && canRequestPOD(mstone) && (
                      <div style={{ display: 'flex', flexDirection: 'column' }}>
                        <Box component="span" sx={{ ...classes.textWarning, fontSize: '15px', margin: '8px 0' }}>
                          Please request for your Photo of Delivery to be sent to you.
                        </Box>
                        <Button
                          variant="outlined"
                          color="warning"
                          style={{
                            width: '150px',
                            borderRadius: '40px',
                            fontSize: '15px',
                            textTransform: 'none',
                          }}
                          onClick={handleRequestPOD}
                        >
                          Request POD
                        </Button>
                      </div>
                    )}

                    {/* {SHIPMENT_STATUS.DELIVERED === mstone && isAlreadyRequested && !hasAccepted && <div style={{display: 'flex', flexDirection: 'column'}}>
                    <span style={{color: '#eb9402', fontSize: '15px', margin: '8px 0'}}>POD is successfully requested and will be sent to you once it is ready.</span>
                  </div>} */}
                  </Grid>
                </Grid>
              </Fragment>
            );
          })}
        </Container>
      </Grid>
      <PreviewImage
        allowRatingPod={hasToken && !deliveryStore.tokenScopes?.includes('pod')}
        isPreview={isPreview || podStore.isPreviewImage}
        images={images}
        imageRef={imageRef}
        handleClosePreview={handleClosePreview}
        deliveryStore={deliveryStore}
        setImages={setImages}
        currentSlide={currentSlide}
        setCurrentSlide={setCurrentSlide}
      />
      <Dialog open={open && !isMobile} onClose={handleCloseDialog} fullWidth maxWidth="md">
        {showDialogNext === 1 && !isMobile && (
          <DialogStepOne handleClose={handleCloseDialog} handleNext={(v) => handleNext(v)} closeDialog={closeDialog} />
        )}
        {showDialogNext === 2 && !isMobile && (
          <DialogStepTwo
            handleClose={handleCloseDialog}
            handleNext={(v) => handleNext(v)}
            setHasToken={setHasToken}
            closeDialog={closeDialog}
          />
        )}
        {showDialogNext === 3 && !isMobile && (
          <DialogStepThree handleClose={handleClose} isSuccess={hasToken} setImages={setImages} />
        )}
      </Dialog>
      <Dialog
        open={isThumbDown}
        maxWidth="sm"
        sx={classes.container}
        onClose={closePopUp}
        slotProps={{
          paper: { style: { padding: '16px 0' } },
        }}
      >
        <PODThumbDown handleClose={closePopUp} trackingCode={deliveryStore.trackingCode} />
      </Dialog>
      <Dialog
        open={isOpenFeedback}
        maxWidth="md"
        sx={classes.container}
        fullWidth
        slotProps={{
          paper: { style: { padding: '16px 0' } },
        }}
      >
        <DialogFeedback closeDialog={handleCloseFeedback} isDisplayPackage={false} isDisplayMenu={false} />
      </Dialog>
    </>
  );
}

const EventCompose = compose(inject('store'), observer)(Event);

export default EventCompose;
